// Help center (D2) — in-app operator guide. Master-detail over the bilingual
// articles in lib/help-content.ts: searchable, section-grouped list on the left,
// the selected article on the right. Tamil-first via use_language().

import { useMemo, useState, type ReactElement } from 'react';
import { use_language } from '../i18n/index.js';
import { HELP_SECTIONS, ARTICLES, type HelpArticle, type HelpBlock } from '../lib/help-content.js';

function ArticleBody({ block, ta }: { block: HelpBlock; ta: boolean }): ReactElement {
  const tf = ta ? 'tamil' : '';
  if (block.kind === 'p') {
    return <p className={`text-sm leading-relaxed text-ink-2 ${tf}`}>{ta ? block.ta : block.en}</p>;
  }
  if (block.kind === 'tip') {
    return (
      <div className={`rounded-lg border border-primary/20 bg-primary/5 px-4 py-3 text-sm text-ink-2 ${tf}`}>
        <span className="font-medium text-primary">{ta ? 'குறிப்பு · ' : 'Tip · '}</span>
        {ta ? block.ta : block.en}
      </div>
    );
  }
  // steps
  const items = ta ? block.ta : block.en;
  return (
    <ol className="space-y-2">
      {items.map((s, i) => (
        <li key={i} className={`flex gap-3 text-sm text-ink-2 ${tf}`}>
          <span className="shrink-0 w-6 h-6 rounded-full bg-bg-elev text-primary text-xs font-semibold flex items-center justify-center">{i + 1}</span>
          <span className="pt-0.5">{s}</span>
        </li>
      ))}
    </ol>
  );
}

export function HelpPage(): ReactElement {
  const lang = use_language();
  const ta = lang === 'ta';
  const tf = ta ? 'tamil' : '';
  const [query, set_query] = useState('');
  const [slug, set_slug] = useState<string>(ARTICLES[0]!.slug);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ARTICLES;
    return ARTICLES.filter((a) =>
      [a.title_en, a.title_ta, a.summary_en, a.summary_ta].some((s) => s.toLowerCase().includes(q)),
    );
  }, [query]);

  const active: HelpArticle = ARTICLES.find((a) => a.slug === slug) ?? ARTICLES[0]!;

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className={`text-2xl font-semibold text-primary-ink ${tf}`}>{ta ? 'உதவி & வழிகாட்டி' : 'Help & guide'}</h1>
        <p className={`mt-1 text-sm text-ink-3 ${tf}`}>{ta ? 'ChitDesk-ஐ நடத்துவது குறித்த படிப்படியான வழிகாட்டுதல்.' : 'Step-by-step guides for running your business on ChitDesk.'}</p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[300px_1fr] items-start">
        {/* List */}
        <aside className="lg:sticky lg:top-4">
          <input
            type="search"
            value={query}
            onChange={(e) => set_query(e.target.value)}
            placeholder={ta ? 'தேடுங்கள்…' : 'Search…'}
            className={`w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm mb-4 outline-none focus:border-primary ${tf}`}
          />
          <nav className="space-y-5">
            {HELP_SECTIONS.map((sec) => {
              const arts = filtered.filter((a) => a.section === sec.key);
              if (arts.length === 0) return null;
              return (
                <div key={sec.key}>
                  <div className={`text-[11px] uppercase tracking-wider text-ink-3 mb-1.5 ${tf}`}>{ta ? sec.title_ta : sec.title_en}</div>
                  <ul className="space-y-0.5">
                    {arts.map((a) => (
                      <li key={a.slug}>
                        <button
                          type="button"
                          onClick={() => set_slug(a.slug)}
                          className={`w-full text-left px-3 py-2 rounded-lg text-sm transition ${tf} ${a.slug === active.slug ? 'bg-primary/10 text-primary font-medium' : 'text-ink-2 hover:bg-bg-elev'}`}
                        >
                          {ta ? a.title_ta : a.title_en}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            {filtered.length === 0 && (
              <p className={`text-sm text-ink-3 ${tf}`}>{ta ? 'எதுவும் கிடைக்கவில்லை.' : 'No matching articles.'}</p>
            )}
          </nav>
        </aside>

        {/* Article */}
        <article className="rounded-2xl border border-line bg-surface p-7 max-w-2xl">
          <h2 className={`text-xl font-semibold text-primary-ink ${tf}`}>{ta ? active.title_ta : active.title_en}</h2>
          <p className={`mt-1.5 text-sm text-ink-3 ${tf}`}>{ta ? active.summary_ta : active.summary_en}</p>
          <div className="mt-6 space-y-5">
            {active.body.map((block, i) => (
              <ArticleBody key={i} block={block} ta={ta} />
            ))}
          </div>
        </article>
      </div>
    </div>
  );
}
